// One observer / one listener per document, fanned out to the elements that asked — instead of
// one per node.
//
// The builder mounts a controller on every node, and each of those used to construct its own
// `ResizeObserver`, its own `MutationObserver` and its own document-level `scroll` listener. The
// work is identical for every subscriber, so the only thing that scaled with page size was the
// browser's own bookkeeping: on a 673-element page that is thousands of registrations to walk on
// every scroll and every layout pass. These channels keep exactly ONE registration per document
// and dispatch to the subscribers of the target that changed, so the steady-state observer and
// listener count is O(1) in page size rather than O(nodes).
//
// `gap_cache.js` already runs a single observer for the whole canvas; this is the same idea,
// generalized so per-node code can share one.
//
// Frame-agnostic, like `portal_helpers.js`: everything is keyed on the target's own
// `ownerDocument`, because the node controllers run inside the preview iframe whose document is
// not this realm's `document`.
//
// LIMIT — the mutation channels dispatch by `record.target`, so they are only correct for
// observation WITHOUT `subtree: true`. With a subtree the record's target is the mutated parent
// rather than the observed ancestor, and every subscribed ancestor would have to be walked to;
// that mode is deliberately not offered here.

// Per-document channels. Weak, so a document that goes away takes its channels with it.
const documentChannels = new WeakMap()

const channelsFor = (doc) => {
  let channels = documentChannels.get(doc)
  if (!channels) {
    documentChannels.set(
      doc,
      (channels = { resize: null, mutations: new Map(), viewport: null })
    )
  }
  return channels
}

// Run `fn`, keeping a failure from taking out the rest of the batch it belongs to. Anything that
// fans out to unrelated owners — a shared observer here, the reposition batch in
// portaled_controls.js — needs this: with a per-node observer an exception could only ever take
// out its own node, and keeping that isolation is what makes sharing safe. Re-throw
// asynchronously so the error still reaches window.onerror / Sentry instead of being quietly
// swallowed.
export const runIsolated = (fn) => {
  try {
    return fn()
  } catch (error) {
    setTimeout(() => {
      throw error
    })
  }
}

// Run every subscriber even if one of them throws.
//
// The subscriber set is snapshotted first, so a batch is the subscribers that were registered
// when it started: a callback is free to subscribe or unsubscribe without deciding, halfway
// through, who else in the same event gets called.
const fanOut = (subscribers, ...args) => {
  if (!subscribers?.size) return

  for (const callback of [...subscribers]) {
    runIsolated(() => callback(...args))
  }
}

/* ============================ element resize ============================ */

// Every subscriber to `el`'s box, across both callers. One `ResizeObserver` per document holds
// all of them.
//
// NOTE on ResizeObserver's initial callback: the observer delivers one the moment a target is
// first observed, and that only happens on the FIRST subscription to a given element — a second
// subscriber joins an element that is already being observed and gets nothing until the box
// actually changes. Neither caller depends on it (both measure eagerly when they start tracking,
// and the gap resizer explicitly filters the initial fire out again), but a new caller that does
// must measure for itself rather than wait for it.
export const observeResize = (el, callback) => {
  const doc = el?.ownerDocument
  const view = doc?.defaultView
  if (!view?.ResizeObserver) return

  const channels = channelsFor(doc)
  if (!channels.resize) {
    const subscribers = new Map()
    const observer = new view.ResizeObserver((entries) => {
      // A target can appear more than once in a batch; every subscriber here is idempotent, so
      // collapse the duplicates instead of repeating the work.
      const notified = new Set()
      for (const entry of entries) {
        if (notified.has(entry.target)) continue
        notified.add(entry.target)
        fanOut(subscribers.get(entry.target))
      }
    })
    channels.resize = { observer, subscribers }
  }

  const { observer, subscribers } = channels.resize
  let forEl = subscribers.get(el)
  if (!forEl) {
    subscribers.set(el, (forEl = new Set()))
    observer.observe(el)
  }
  forEl.add(callback)
}

export const unobserveResize = (el, callback) => {
  const channel = documentChannels.get(el?.ownerDocument)?.resize
  const forEl = channel?.subscribers.get(el)
  if (!forEl) return

  forEl.delete(callback)
  if (forEl.size) return

  // Last subscriber for this element: stop observing it, so the map can't hold a removed node
  // (and its subtree) alive.
  channel.subscribers.delete(el)
  channel.observer.unobserve(el)
}

/* ============================== mutations ============================== */

// A MutationObserver has no per-target `unobserve` — only `disconnect()`, which would drop every
// other subscriber — so unsubscribing drops the callback and lets that target's records fall on
// the floor until the channel itself is torn down. That is cheap: the spec holds an observer's
// targets weakly (so this cannot keep a removed node alive), records are only queued for targets
// that are still registered, and the last subscriber out disconnects the whole channel.
//
// Channels are keyed by name rather than by their options object so that two callers watching
// different things (class attributes vs. child lists) never share an observer and never see each
// other's records.
const mutationChannel = (doc, key) => {
  const view = doc?.defaultView
  if (!view?.MutationObserver) return null

  const channels = channelsFor(doc)
  let channel = channels.mutations.get(key)
  if (channel) return channel

  const subscribers = new Map()
  const observer = new view.MutationObserver((records) => {
    // Group by target before dispatching: a subscriber that used to own its observer only ever
    // saw its OWN records, and the filters written against that (the gap resizer's
    // `touchesNodeChild`) still expect exactly that.
    const byTarget = new Map()
    for (const record of records) {
      let forTarget = byTarget.get(record.target)
      if (!forTarget) byTarget.set(record.target, (forTarget = []))
      forTarget.push(record)
    }
    for (const [target, targetRecords] of byTarget) {
      fanOut(subscribers.get(target), targetRecords)
    }
  })

  channels.mutations.set(key, (channel = { observer, subscribers }))
  return channel
}

const observeMutations = (el, key, options, callback) => {
  const channel = mutationChannel(el?.ownerDocument, key)
  if (!channel) return

  let forEl = channel.subscribers.get(el)
  if (!forEl) {
    channel.subscribers.set(el, (forEl = new Set()))
    // Re-observing a target this observer already watches replaces its registration rather than
    // adding a second one, so a reconnect can call this again safely.
    channel.observer.observe(el, options)
  }
  forEl.add(callback)
}

const unobserveMutations = (doc, key, el, callback) => {
  const channels = documentChannels.get(doc)
  const channel = channels?.mutations.get(key)
  const forEl = channel?.subscribers.get(el)
  if (!forEl) return

  forEl.delete(callback)
  if (forEl.size) return
  channel.subscribers.delete(el)

  if (channel.subscribers.size) return
  channel.observer.disconnect()
  channels.mutations.delete(key)
}

const CLASS_CHANGES = { attributes: true, attributeFilter: ['class'] }

// Watch `el`'s own class attribute. `callback` receives that element's records.
export const observeClassChanges = (el, callback) =>
  observeMutations(el, 'class', CLASS_CHANGES, callback)

export const unobserveClassChanges = (el, callback) =>
  unobserveMutations(el?.ownerDocument, 'class', el, callback)

const CHILD_LIST = { childList: true }

// Watch `el`'s direct children being added or removed. `callback` receives that element's records.
export const observeChildList = (el, callback) =>
  observeMutations(el, 'childList', CHILD_LIST, callback)

export const unobserveChildList = (el, callback) =>
  unobserveMutations(el?.ownerDocument, 'childList', el, callback)

/* ============================== viewport =============================== */

// Anything that moves every measured box at once: a scroll in any scroll container (captured,
// since `scroll` does not bubble) or a window resize. One pair of listeners per document,
// attached while at least one subscriber wants them.
export const onViewportChange = (doc, callback) => {
  const view = doc?.defaultView
  if (!view) return

  const channels = channelsFor(doc)
  if (!channels.viewport) {
    const subscribers = new Set()
    channels.viewport = { subscribers, handler: () => fanOut(subscribers) }
  }

  const { subscribers, handler } = channels.viewport
  if (!subscribers.size) {
    doc.addEventListener('scroll', handler, { capture: true })
    view.addEventListener('resize', handler)
  }
  subscribers.add(callback)
}

export const offViewportChange = (doc, callback) => {
  const channel = documentChannels.get(doc)?.viewport
  if (!channel?.subscribers.delete(callback)) return
  if (channel.subscribers.size) return

  doc.removeEventListener('scroll', channel.handler, { capture: true })
  doc.defaultView?.removeEventListener('resize', channel.handler)
}
